import {
  FETCH_NOTIFICATIONS_LOADING,
  FETCH_NOTIFICATIONS,
  FETCH_NOTIFICATIONS_FAIL,
  FETCH_NOTIFICATION_BY_ID,
  FETCH_NOTIFICATION_BY_ID_FAIL,
  MARK_NOTIFICATION_READ,
  MARK_NOTIFICATION_READ_FAIL,
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_ALL_NOTIFICATIONS_READ_FAIL,
  TOGGLE_NOTIFICATION_STAR,
  TOGGLE_NOTIFICATION_STAR_FAIL,
  FETCH_UNREAD_COUNT,
  CLEAR_NOTIFICATIONS_STATE,
  FETCH_PREFERENCE_EVENT_TYPES,
  FETCH_PREFERENCE_EVENT_TYPES_FAIL,
  FETCH_PREFERENCES_LOADING,
  FETCH_PREFERENCES,
  FETCH_PREFERENCES_FAIL,
  UPDATE_PREFERENCES,
  UPDATE_PREFERENCES_FAIL,
  FETCH_DISTRIBUTION_LOADING,
  FETCH_DISTRIBUTION,
  FETCH_DISTRIBUTION_FAIL,
  UPDATE_DISTRIBUTION,
  UPDATE_DISTRIBUTION_FAIL,
  UPLOAD_DISTRIBUTION,
  UPLOAD_DISTRIBUTION_FAIL,
  DOWNLOAD_DISTRIBUTION_LOADING,
  DOWNLOAD_DISTRIBUTION,
  DOWNLOAD_DISTRIBUTION_FAIL,
  CLEAR_DISTRIBUTION_STATE,
  FETCH_ORG_DEPT_ROLES,
  FETCH_ORG_DEPT_ROLES_FAIL,
  FETCH_CMO_DEPT_ROLES,
  FETCH_CMO_DEPT_ROLES_FAIL,
  FETCH_NOTIFICATION_CATEGORIES,
  FETCH_ROLES_BY_EVENT,
  FETCH_ROLES_BY_EVENT_LOADING,
  FETCH_ROLES_BY_EVENT_FAIL,
} from "../../actions/types";

const initialState = {
  // ── Notification dashboard ─────────────────────────────────────────────────
  notifications: [],
  pagination: { total: 0, page: 1, pageSize: 20, totalPages: 1 },
  unreadCount: 0,
  lastSyncedAt: null,
  fetchLoading: false,
  fetchError: false,
  markAllSuccess: false,
  markAllError: false,
  starToggleError: false,
  // ── Manage Notifications (preferences) ────────────────────────────────────
  // preferenceEventTypes: flat array of { event_type_id, event_name, category }
  preferenceEventTypes: [],
  preferenceEventTypesLoading: false,
  preferenceEventTypesError: false,
  // preferences: map of event_type_id → { dashboard_notification, email_notification }
  preferences: {},
  preferencesLoading: false,
  preferencesError: false,
  updatePreferencesSuccess: false,
  updatePreferencesError: false,
  // ── Manage Distribution List ───────────────────────────────────────────────
  // categories from DB (dynamic filter options)
  categories: [],
  // distribution: array of { event_type_id, event_name, category, last_updated_by, last_updated_on, roles }
  distribution: [],
  distributionLoading: true,
  distributionError: false,
  // Per-event roles (fetched on view/edit click)
  eventRoles: [],
  eventRolesLoading: false,
  eventRolesError: false,
  updateDistributionSuccess: false,
  updateDistributionError: false,
  uploadDistributionSuccess: false,
  uploadDistributionError: false,
  downloadDistributionLoading: false,
  downloadDistributionBlob: null,
  downloadDistributionError: false,
  // ── Org / Dept / Role lookup (distribution edit popup) ───────────────────
  orgDeptRoles: null,
  orgDeptRolesError: false,
};

export default function notificationsReducer(state = initialState, action) {
  switch (action.type) {

    case FETCH_NOTIFICATIONS_LOADING:
      return { ...state, fetchLoading: true, fetchError: false };

    // ── Fetch list ────────────────────────────────────────────────────────────
    // API response: { status, data: [...], total, page, pageSize }
    // Normalize: user_event_id → id so all action endpoints use n.id
    case FETCH_NOTIFICATIONS: {
      const { data = [], total = 0, page = 1, pageSize = 20 } = action.payload;
      const normalized = data.map((n) => ({ ...n, id: n.user_event_id }));
      return {
        ...state,
        notifications: normalized,
        pagination: {
          total,
          page,
          pageSize,
          totalPages: Math.max(1, Math.ceil(total / pageSize)),
        },
        unreadCount: state.unreadCount,
        lastSyncedAt: new Date().toISOString(),
        fetchLoading: false,
        fetchError: false,
      };
    }
    case FETCH_NOTIFICATIONS_FAIL:
      return { ...state, fetchLoading: false, fetchError: true };

    // ── Fetch single notification (on chevron expand) ─────────────────────────
    // Merges fresh data from server into the existing list row.
    // IMPORTANT: on chevron-expand we dispatch fetchNotificationById AND
    // markNotificationRead in parallel. If the detail response arrives AFTER
    // the optimistic mark-as-read reducer tick, a naive `{ ...n, ...fresh }`
    // would overwrite is_read=true with the server's stale is_read=false,
    // causing the reported "sometimes one expand, sometimes two" bug.
    // We preserve locally-optimistic fields (is_read/read_at, is_starred/starred_at)
    // so the client state is never reverted by an in-flight read.
    case FETCH_NOTIFICATION_BY_ID: {
      const fresh = { ...action.payload, id: action.payload.user_event_id };
      return {
        ...state,
        notifications: state.notifications.map((n) => {
          if (n.id !== fresh.id) return n;
          return {
            ...n,
            ...fresh,
            // once marked read locally, don't let stale server data revert it
            is_read: n.is_read || fresh.is_read || false,
            read_at: n.read_at || fresh.read_at || null,
            // star is always toggled client-optimistically — keep local truth
            is_starred: typeof n.is_starred === 'boolean' ? n.is_starred : fresh.is_starred,
            starred_at: n.is_starred ? (n.starred_at || fresh.starred_at) : null,
          };
        }),
      };
    }
    case FETCH_NOTIFICATION_BY_ID_FAIL:
      return state;

    // ── Unread count ──────────────────────────────────────────────────────────
    case FETCH_UNREAD_COUNT:
      return { ...state, unreadCount: action.payload };

    // ── Mark single read ──────────────────────────────────────────────────────
    case MARK_NOTIFICATION_READ: {
      const { id } = action.payload;
      const updated = state.notifications.map((n) =>
        n.id === id
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n
      );
      return {
        ...state,
        notifications: updated,
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }
    case MARK_NOTIFICATION_READ_FAIL:
      return state;

    // ── Mark all read ─────────────────────────────────────────────────────────
    case MARK_ALL_NOTIFICATIONS_READ:
      return {
        ...state,
        notifications: state.notifications.map((n) => ({
          ...n,
          is_read: true,
          read_at: n.read_at || new Date().toISOString(),
        })),
        unreadCount: 0,
        markAllSuccess: true,
        markAllError: false,
      };
    case MARK_ALL_NOTIFICATIONS_READ_FAIL:
      return { ...state, markAllSuccess: false, markAllError: true };

    // ── Toggle star ───────────────────────────────────────────────────────────
    // API response: { status: true, isStarred: boolean }
    case TOGGLE_NOTIFICATION_STAR: {
      const { id, isStarred } = action.payload;
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, is_starred: isStarred } : n
        ),
      };
    }
    case TOGGLE_NOTIFICATION_STAR_FAIL:
      return { ...state, starToggleError: true };

    // ── Clear flags ───────────────────────────────────────────────────────────
    case CLEAR_NOTIFICATIONS_STATE:
      return {
        ...state,
        markAllSuccess: false,
        markAllError: false,
        starToggleError: false,
        fetchError: false,
        updatePreferencesSuccess: false,
        updatePreferencesError: false,
      };

    // ── Preference event types ─────────────────────────────────────────────
    // API: { status, data: [{ event_type_id, event_name, category }] }
    case FETCH_PREFERENCE_EVENT_TYPES:
      return {
        ...state,
        preferenceEventTypes: Array.isArray(action.payload) ? action.payload : [],
        preferenceEventTypesLoading: false,
        preferenceEventTypesError: false,
      };
    case FETCH_PREFERENCE_EVENT_TYPES_FAIL:
      return { ...state, preferenceEventTypesLoading: false, preferenceEventTypesError: true };

    // ── User preferences ──────────────────────────────────────────────────
    case FETCH_PREFERENCES_LOADING:
      return { ...state, preferencesLoading: true, preferencesError: false };

    // API: [{ objectType, eventNotifications: [{ eventToUserMappingID, eventTypeID, eventType, dashboardNotification, emailNotification, ... }] }]
    // Multiple rows per eventTypeID (history) — keep only the latest per eventTypeID
    case FETCH_PREFERENCES: {
      const groups = action.payload || [];
      // Collect the latest record per eventTypeID across all groups
      const latestByEventType = {};
      groups.forEach((group) => {
        (group.eventNotifications || []).forEach((en) => {
          const existing = latestByEventType[en.eventTypeID];
          if (!existing || en.lastUpdatedDate > existing.lastUpdatedDate) {
            latestByEventType[en.eventTypeID] = { ...en, category: group.objectType };
          }
        });
      });
      const flatEvents = [];
      const prefMap = {};
      Object.values(latestByEventType).forEach((en) => {
        flatEvents.push({
          eventTypeID: en.eventTypeID,
          eventTypeName: en.eventType,
          eventDescription: en.eventDescription || '',
          category: en.category,
        });
        prefMap[en.eventTypeID] = {
          eventToUserMappingID: en.eventToUserMappingID,
          dashboard_notification: en.dashboardNotification,
          email_notification: en.emailNotification,
        };
      });
      return {
        ...state,
        preferenceEventTypes: flatEvents,
        preferences: prefMap,
        preferencesLoading: false,
        preferencesError: false,
        preferenceEventTypesLoading: false,
        preferenceEventTypesError: false,
      };
    }
    case FETCH_PREFERENCES_FAIL:
      return { ...state, preferencesLoading: false, preferencesError: true };

    // UPDATE_PREFERENCES: optimistically apply the mappings to local state
    case UPDATE_PREFERENCES: {
      const updated = { ...state.preferences };
      (action.payload || []).forEach((m) => {
        let key;
        if (m.eventToUserMappingID) {
          // Match by eventToUserMappingID for existing preferences
          key = Object.keys(updated).find(
            (k) => updated[k].eventToUserMappingID === m.eventToUserMappingID
          );
        }
        if (!key && m.eventTypeID != null) {
          // Match by eventTypeID for new preferences without a mapping ID
          key = String(m.eventTypeID);
        }
        if (key) {
          updated[key] = {
            ...updated[key],
            dashboard_notification: m.dashboardNotification,
            email_notification: m.emailNotification,
          };
        }
      });
      return {
        ...state,
        preferences: updated,
        updatePreferencesSuccess: true,
        updatePreferencesError: false,
      };
    }
    case UPDATE_PREFERENCES_FAIL:
      return { ...state, updatePreferencesSuccess: false, updatePreferencesError: true };

    // ── Distribution list ─────────────────────────────────────────────────
    case FETCH_DISTRIBUTION_LOADING:
      return { ...state, distributionLoading: true, distributionError: false };

    // API returns: { eventNotificationList: [{ objectType, eventTypes: [{ eventTypeID, roles, ...flags }] }] }
    case FETCH_DISTRIBUTION: {
      const rawList = action.payload.eventNotificationList || [];
      const list = rawList.map((group) => ({
        objectType: group.objectType || 'Other',
        eventNotifications: (group.eventTypes || []).map((ev) => ({
          eventTypeID: ev.eventTypeID,
          eventTypeName: ev.eventTypeName || `Event ${ev.eventTypeID}`,
          eventDescription: ev.eventDescription || '',
          objectType: group.objectType,
          brandUserAssociationReqd: ev.brandUserAssociationReqd || false,
          cmoUserAssociationReqd: ev.cmoUserAssociationReqd || false,
          productTypeUserAssociationReqd: ev.productTypeUserAssociationReqd || false,
          lastUpdatedBy: ev.lastUpdatedBy || null,
          lastUpdatedDate: ev.lastUpdatedDate || null,
          roles: (ev.roles || []).map((r) => ({
            eventToRoleMappingID: r.eventToRoleMappingID,
            collaboratorRoleID: r.collaboratorRoleID,
            collaboratorRoleName: r.collaboratorRoleName || null,
            collaboratorTypeID: r.collaboratorTypeID || null,
            departmentID: r.departmentID || null,
            departmentName: r.departmentName || null,
            vendorMosymphonyID: r.vendorMosymphonyID || null,
            actionRequired: r.actionRequired || null,
            markForDelete: r.markForDelete || false,
          })),
        })),
      }));
      return {
        ...state,
        distribution: list,
        distributionLoading: false,
        distributionError: false,
      };
    }
    case FETCH_DISTRIBUTION_FAIL:
      return { ...state, distributionLoading: false, distributionError: true };

    case UPDATE_DISTRIBUTION:
      return {
        ...state,
        updateDistributionSuccess: true,
        updateDistributionError: false,
      };
    case UPDATE_DISTRIBUTION_FAIL:
      return { ...state, updateDistributionSuccess: false, updateDistributionError: true };

    case UPLOAD_DISTRIBUTION:
      return {
        ...state,
        uploadDistributionSuccess: true,
        uploadDistributionError: false,
      };
    case UPLOAD_DISTRIBUTION_FAIL:
      return { ...state, uploadDistributionSuccess: false, uploadDistributionError: true };

    case DOWNLOAD_DISTRIBUTION_LOADING:
      return { ...state, downloadDistributionLoading: true, downloadDistributionBlob: null, downloadDistributionError: false };
    case DOWNLOAD_DISTRIBUTION:
      return { ...state, downloadDistributionLoading: false, downloadDistributionBlob: action.payload, downloadDistributionError: false };
    case DOWNLOAD_DISTRIBUTION_FAIL:
      return { ...state, downloadDistributionLoading: false, downloadDistributionBlob: null, downloadDistributionError: true };

    // ── Clear distribution state flags ─────────────────────────────────────
    case CLEAR_DISTRIBUTION_STATE:
      return {
        ...state,
        updateDistributionSuccess: false,
        updateDistributionError: false,
        uploadDistributionSuccess: false,
        uploadDistributionError: false,
        downloadDistributionLoading: false,
        downloadDistributionBlob: null,
        downloadDistributionError: false,
      };

    // ── Org / Dept / Role lookup ──────────────────────────────────────────
    case FETCH_ORG_DEPT_ROLES:
      return { ...state, orgDeptRoles: action.payload, orgDeptRolesError: false };
    case FETCH_ORG_DEPT_ROLES_FAIL:
      return { ...state, orgDeptRolesError: true };

    // ── CMO Dept / Role lookup ───────────────────────────────────────────
    case FETCH_CMO_DEPT_ROLES:
      return { ...state, cmoDeptRoles: action.payload, cmoDeptRolesError: false };
    case FETCH_CMO_DEPT_ROLES_FAIL:
      return { ...state, cmoDeptRolesError: true };

    case FETCH_NOTIFICATION_CATEGORIES:
      return { ...state, categories: action.payload || [] };

    case FETCH_ROLES_BY_EVENT_LOADING:
      return { ...state, eventRolesLoading: true, eventRolesError: false };
    case FETCH_ROLES_BY_EVENT:
      return { ...state, eventRoles: action.payload || [], eventRolesLoading: false, eventRolesError: false };
    case FETCH_ROLES_BY_EVENT_FAIL:
      return { ...state, eventRolesLoading: false, eventRolesError: true };

    default:
      return state;
  }
}
